import { ApiConfig } from "../apiConfig/apiConfig";
import { ApiCallDelete, ApiCallGet, ApiCallPost } from "../apiConfig/apiCall";
import AuthService from "./AuthService";
import { STATIC_WITHDRAW_COINS } from "./withdrawStaticCoins";
import { canonicalWithdrawalChainFromUiCode } from "../../ui/Pages/WithdrawPage/withdrawPageHelpers";

const bearerHeaders = () => {
  const raw = localStorage.getItem("token");
  const token =
    raw && String(raw).trim() !== ""
      ? String(raw).startsWith("Bearer ")
        ? raw.trim()
        : `Bearer ${String(raw).trim()}`
      : null;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };
};

function extractList(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.coins)) return d.coins;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.list)) return d.list;
  return [];
}

export function extractAddressBookList(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.rows)) return d.rows;
  if (d && Array.isArray(d.addresses)) return d.addresses;
  return [];
}

/** Parse `GET /api/v1/wallet/withdrawal-address-history` list. */
export function extractWithdrawalAddressHistoryList(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  return [];
}

/** `GET /api/v1/wallet/withdrawal-address-history` */
export async function fetchWithdrawalAddressHistory() {
  const { baseWalletV1Api, walletWithdrawalAddressHistory } = ApiConfig;
  const url = baseWalletV1Api + walletWithdrawalAddressHistory;
  return ApiCallGet(url, bearerHeaders());
}

/** `GET /api/v1/wallet/withdrawal-coins` */
export async function fetchWithdrawalCoinsV1() {
  const { baseWalletV1Api, walletWithdrawalCoins } = ApiConfig;
  const url = baseWalletV1Api + walletWithdrawalCoins;
  return ApiCallGet(url, bearerHeaders());
}

/** `GET /api/v1/wallet/address-book` — optional `chain`, `coin`, `status` query filters */
export async function fetchWalletAddressBook(query = {}) {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const q = new URLSearchParams();
  if (query.chain != null && String(query.chain).trim()) q.set("chain", String(query.chain).trim());
  if (query.coin != null && String(query.coin).trim()) q.set("coin", String(query.coin).trim());
  if (query.status != null && String(query.status).trim()) q.set("status", String(query.status).trim());
  const qs = q.toString();
  const url = `${baseWalletV1Api}${walletAddressBook}${qs ? `?${qs}` : ""}`;
  return ApiCallGet(url, bearerHeaders());
}

/** `GET /api/v1/wallet/withdrawal-24h-usage?coinName=` */
export async function fetchWithdrawal24hUsage(coinName) {
  const coin = String(coinName || "").trim();
  if (!coin) return { success: false, data: null };
  const { baseWalletV1Api, walletWithdrawal24hUsage } = ApiConfig;
  const url = `${baseWalletV1Api}${walletWithdrawal24hUsage}?coinName=${encodeURIComponent(coin)}`;
  return ApiCallGet(url, bearerHeaders());
}

/** `GET /api/v1/wallet/passkey-withdrawal-challenge` */
export async function fetchPasskeyWithdrawalChallenge() {
  const { baseWalletV1Api, walletPasskeyWithdrawalChallenge } = ApiConfig;
  const url = baseWalletV1Api + walletPasskeyWithdrawalChallenge;
  return ApiCallGet(url, bearerHeaders());
}

/** `POST /api/v1/wallet/address-book` */
export async function addWalletAddressBook(body) {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = baseWalletV1Api + walletAddressBook;
  return ApiCallPost(url, body, bearerHeaders());
}

/** `DELETE /api/v1/wallet/address-book/:id` */
export async function deleteWalletAddressBook(id) {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = `${baseWalletV1Api}${walletAddressBook}/${encodeURIComponent(String(id))}`;
  return ApiCallDelete(url, bearerHeaders());
}

/** `POST /api/v1/wallet/address-book/initiate` — start Satoshi or MetaMask whitelist flow */
export async function initiateAddressBookWhitelist(body) {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = `${baseWalletV1Api}${walletAddressBook}/initiate`;
  return ApiCallPost(url, body, bearerHeaders());
}

/** `GET /api/v1/wallet/address-book/verification-options` — 2FA methods before initiating add */
export async function fetchAddressBookVerificationOptions() {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = `${baseWalletV1Api}${walletAddressBook}/verification-options`;
  return ApiCallGet(url, bearerHeaders());
}

/** `POST /api/v1/wallet/address-book/send-otp` — email or SMS OTP before initiate */
export async function sendAddressBookVerificationOtp(body) {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = `${baseWalletV1Api}${walletAddressBook}/send-otp`;
  return ApiCallPost(url, body, bearerHeaders());
}

/** `POST /api/v1/wallet/address-book/passkey-challenge` — WebAuthn assertion options */
export async function fetchAddressBookPasskeyChallenge() {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = `${baseWalletV1Api}${walletAddressBook}/passkey-challenge`;
  return ApiCallPost(url, {}, bearerHeaders());
}

/** `POST /api/v1/wallet/address-book/:id/confirm-satoshi` — poll after user sent micro-deposit */
export async function confirmSatoshiAddressBook(id) {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = `${baseWalletV1Api}${walletAddressBook}/${encodeURIComponent(String(id))}/confirm-satoshi`;
  return ApiCallPost(url, {}, bearerHeaders());
}

/** `POST /api/v1/wallet/address-book/:id/verify-signature` — submit MetaMask signature */
export async function verifySignatureAddressBook(id, signature) {
  const { baseWalletV1Api, walletAddressBook } = ApiConfig;
  const url = `${baseWalletV1Api}${walletAddressBook}/${encodeURIComponent(String(id))}/verify-signature`;
  return ApiCallPost(url, { signature }, bearerHeaders());
}

/** Single in-flight `fetchWithdrawCoins` so mount / StrictMode / re-entry does not double-hit. */
let fetchWithdrawCoinsInFlight = null;

function pickFirstFiniteNum(obj, keys) {
  if (!obj || typeof obj !== "object") return null;
  for (const k of keys) {
    const v = obj[k];
    if (v == null || v === "") continue;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Map common withdrawal-coins balance fields onto `balance` / `locked_balance` for `formatFundAvailable`.
 */
function withdrawalCoinBalancePatch(raw) {
  if (!raw || typeof raw !== "object") return {};
  const avail = pickFirstFiniteNum(raw, [
    "available_balance",
    "availableBalance",
    "withdrawable_balance",
    "withdrawableBalance",
    "withdrawable",
    "spot_balance",
    "spotBalance",
    "main_balance",
    "mainBalance",
    "wallet_balance",
    "walletBalance",
    "free_balance",
    "freeBalance",
    "balance_free",
    "balanceFree",
  ]);
  const locked = pickFirstFiniteNum(raw, ["locked_balance", "lockedBalance", "locked", "freeze_balance", "freezeBalance"]) ?? 0;
  const legacyBal = pickFirstFiniteNum(raw, ["balance", "asset_balance", "assetBalance"]);
  const patch = {};
  if (avail != null) {
    patch.balance = String(avail);
    patch.locked_balance = String(locked);
  } else if (legacyBal != null) {
    patch.balance = String(legacyBal);
    patch.locked_balance = String(locked);
  }
  return patch;
}

/**
 * Maps `GET /api/v1/wallet/withdrawal-coins` rows (`chains[]` objects) into the shape `getWithdrawNetworks()` expects.
 */
function normalizeWithdrawalCoinRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (!Array.isArray(raw.chains) || raw.chains.length === 0 || typeof raw.chains[0] !== "object") {
    return { ...raw, ...withdrawalCoinBalancePatch(raw) };
  }
  const chainCodes = [];
  const minW = {};
  const maxW = {};
  const feeMap = {};
  const statusMap = {};
  const chainFullNames = {};
  const tokenAssetIds = {};

  for (const ch of raw.chains) {
    const code = String(ch.chain || ch.network || "").trim();
    if (!code) continue;
    chainCodes.push(code);
    if (ch.min_withdrawal != null) minW[code] = ch.min_withdrawal;
    if (ch.max_withdrawal != null) maxW[code] = ch.max_withdrawal;
    if (ch.withdrawal_fee != null) feeMap[code] = ch.withdrawal_fee;
    if (ch.withdrawal_status != null) statusMap[code] = ch.withdrawal_status;
    if (ch.chain_full_name) chainFullNames[code] = String(ch.chain_full_name);
    if (ch.tokenAssetId != null && String(ch.tokenAssetId).trim()) tokenAssetIds[code] = String(ch.tokenAssetId).trim();
  }

  return {
    ...raw,
    chain: chainCodes,
    min_withdrawal: minW,
    max_withdrawal: maxW,
    withdrawal_fee: feeMap,
    withdrawal_status: statusMap,
    chain_full_names: chainFullNames,
    token_asset_ids: tokenAssetIds,
    chains_detail: raw.chains,
    ...withdrawalCoinBalancePatch(raw),
  };
}

/**
 * Withdraw catalog: `GET /api/v1/wallet/withdrawal-coins` only (Bearer).
 * Localhost falls back to bundled static rows when the endpoint is empty or fails (offline UI).
 */
export async function fetchWithdrawCoins() {
  if (fetchWithdrawCoinsInFlight) return fetchWithdrawCoinsInFlight;

  const run = (async () => {
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

    let coinsList = [];
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;

    if (token) {
      try {
        const r1 = await fetchWithdrawalCoinsV1();
        coinsList = extractList(r1).map(normalizeWithdrawalCoinRow).filter(Boolean);
        if (coinsList.length && r1?.success !== false) {
          return { ok: true, coins: coinsList, source: "withdrawal-coins-v1" };
        }
      } catch {
        coinsList = [];
      }
    }

    if (isLocal) {
      return { ok: true, coins: STATIC_WITHDRAW_COINS, source: "static-localhost" };
    }
    return { ok: false, coins: [], source: "none" };
  })();

  fetchWithdrawCoinsInFlight = run;
  return run.finally(() => {
    if (fetchWithdrawCoinsInFlight === run) fetchWithdrawCoinsInFlight = null;
  });
}

/**
 * `GET /v1/wallet/get-wallet-balance?fromWallet=&currencyId=`
 */
export async function fetchWalletBalance(currencyId, fromWallet = "spot") {
  if (currencyId == null || currencyId === "") return { ok: false, balance: 0, raw: null };
  return AuthService.getWalletBalance(currencyId, fromWallet);
}

/**
 * API `wallet_type` for withdraw-page Main Wallet (`GET .../user-wallet?wallet_type=`).
 */
export const WITHDRAW_PAGE_WALLET_TYPE = "main";

/** Single in-flight load — one wallet bucket for withdraw UI. */
let withdrawWalletFundsInFlight = null;

/** `GET /v1/wallet/user-wallet?wallet_type=spot` (one call). */
export async function loadWithdrawWalletFunds() {
  if (withdrawWalletFundsInFlight) return withdrawWalletFundsInFlight;

  const run = (async () => {
    try {
      const res = await AuthService.getUserfunds(WITHDRAW_PAGE_WALLET_TYPE);
      if (res?.success && Array.isArray(res.data)) return { funds: res.data };
    } catch {
      /* ignore */
    }
    return { funds: null };
  })();

  withdrawWalletFundsInFlight = run;
  return run.finally(() => {
    if (withdrawWalletFundsInFlight === run) withdrawWalletFundsInFlight = null;
  });
}

/** Same (email_or_phone) + concurrent send → one request (avoids double POST from StrictMode / multi-modal). */
const sendOtpMinimalPending = new Map();

/**
 * `POST /v1/wallet/verify-withdrawal` — OTP send/verify step for withdrawals (per backend docs).
 * This is the only OTP endpoint Withdraw page should use.
 */
export async function sendOtpMinimal(emailOrPhone) {
  const key = String(emailOrPhone || "").trim();
  if (!key) {
    return { success: false, message: "email_or_phone is required" };
  }
  if (sendOtpMinimalPending.has(key)) {
    return sendOtpMinimalPending.get(key);
  }
  // Backend `verifyWithdrawals` middleware decides whether to send or verify based on body;
  // for "send OTP" we only provide the destination.
  const p = AuthService.postVerifyWithdrawal({ email_or_phone: key }).finally(() => {
    if (sendOtpMinimalPending.get(key) === p) sendOtpMinimalPending.delete(key);
  });
  sendOtpMinimalPending.set(key, p);
  return p;
}

/**
 * `POST /v1/user/send-otp` — extended body for backends that expect `type`, `resend`, etc.
 */
export async function sendWithdrawOtp(emailOrPhone, extra = {}) {
  const { baseAuth, getOtp: otpPath } = ApiConfig;
  const url = baseAuth + otpPath;
  const params = {
    email_or_phone: String(emailOrPhone || "").trim(),
    type: "withdrawal",
    resend: true,
    ...extra,
  };
  return ApiCallPost(url, params, bearerHeaders());
}

/**
 * `POST /api/v1/wallet/withdrawal-request` — lock funds and wait for admin approval.
 * Do not call `POST /api/v1/wallet/withdrawal` from the trader app (that auto-sends Fireblocks).
 * Required: coinName, tokenAssetId, withdrawal_address, amount.
 */
export async function submitWithdrawal(body) {
  const { baseWalletV1Api, walletWithdrawalRequest } = ApiConfig;
  const url = baseWalletV1Api + walletWithdrawalRequest;

  const addrRaw = body.address != null ? body.address : body.withdrawal_address;
  const addr = addrRaw != null ? String(addrRaw).trim() : "";

  const params = {
    coinName: body.coinName,
    tokenAssetId: body.tokenAssetId,
    withdrawal_address: addr || body.withdrawal_address,
    amount: typeof body.amount === "number" ? body.amount : parseFloat(String(body.amount).replace(/,/g, "")),
  };

  if (body.email_or_phone != null) params.email_or_phone = String(body.email_or_phone || "").trim();
  if (body.verification_code != null) params.verification_code = String(body.verification_code ?? "").trim();
  if (body.verification_method) params.verification_method = body.verification_method;
  if (body.email_code != null) params.email_code = String(body.email_code).trim();
  if (body.mobile_code != null) params.mobile_code = String(body.mobile_code).trim();
  if (body.google_authenticator_code != null) {
    params.google_authenticator_code = String(body.google_authenticator_code).trim();
  }

  if (body.fund_password) params.fund_password = body.fund_password;
  if (body.passkey_credential != null) params.passkey_credential = body.passkey_credential;

  if (body.usdt_balance != null) params.usdt_balance = body.usdt_balance;

  return ApiCallPost(url, params, bearerHeaders());
}

/** Alias used by Withdraw page — same as `submitWithdrawal`. */
export const submitWithdrawalRequest = submitWithdrawal;

/**
 * `GET /api/v1/wallet/withdrawals?skip&limit` — includes `awaiting_admin` on each row.
 */
export async function fetchWithdrawalHistory(skip = 0, limit = 20) {
  const { baseWalletV1Api, walletWithdrawals } = ApiConfig;
  const url = `${baseWalletV1Api}${walletWithdrawals}?skip=${encodeURIComponent(skip)}&limit=${encodeURIComponent(limit)}`;
  return ApiCallGet(url, bearerHeaders());
}

/** `GET /v1/transaction/wallet-history?skip&limit` */
export async function fetchWalletHistoryLedger(skip = 0, limit = 20) {
  return AuthService.walletHistoryGet(skip, limit);
}

/** `GET /v1/wallet/fireblocks-deposit-coins` */
export async function fetchFireblocksDepositCoins() {
  return AuthService.getFireblocksDepositCoins();
}

/**
 * `POST /api/v1/wallet/user_cancel_withdrawal` — `{ _id, currency_id }`.
 * Only works while `awaiting_admin` is true (no Fireblocks id yet).
 */
export async function cancelUserWithdrawal({ _id, currency_id, amount, fee } = {}) {
  const { baseWalletV1Api, user_cancel_withdrawal } = ApiConfig;
  const url = baseWalletV1Api + user_cancel_withdrawal;
  const params = { _id, currency_id };
  if (amount != null) params.amount = amount;
  if (fee != null) params.fee = fee;
  return ApiCallPost(url, params, bearerHeaders());
}

/**
 * `POST /v1/wallet/verify-withdrawal` — no Bearer; server/callback use.
 */
export async function postVerifyWithdrawal(body) {
  return AuthService.postVerifyWithdrawal(body);
}

// ── Withdrawal Security Settings ─────────────────────────────────────────────

/** GET /v1/security/withdrawal-settings */
export async function fetchWithdrawalSecuritySettings() {
  const { baseSecurity } = ApiConfig;
  const url = baseSecurity + 'withdrawal-settings';
  return ApiCallGet(url, bearerHeaders());
}

/** POST /v1/security/withdrawal-settings */
export async function updateWithdrawalSecuritySettings(body) {
  const { baseSecurity } = ApiConfig;
  const url = baseSecurity + 'withdrawal-settings';
  return ApiCallPost(url, body, bearerHeaders());
}

/** POST /v1/security/withdrawal-otp  — send OTP for withdrawal verification */
export async function sendWithdrawalVerificationOtp(method) {
  const { baseSecurity } = ApiConfig;
  const url = baseSecurity + 'withdrawal-otp';
  return ApiCallPost(url, { method }, bearerHeaders());
}

/**
 * POST /v1/security/withdrawal-settings/toggle
 * Verify method + toggle a withdrawal security setting.
 * body: { method, enable, code?, fund_password? }
 */
export async function verifyAndToggleWithdrawalSetting(body) {
  const { baseSecurity } = ApiConfig;
  const url = baseSecurity + 'withdrawal-settings/toggle';
  return ApiCallPost(url, body, bearerHeaders());
}

/**
 * POST /api/v1/wallet/validate-address
 * Returns { success, valid, message, source }
 */
export async function validateWithdrawAddress(address, chain, tokenAssetId) {
  const { baseWalletV1Api } = ApiConfig;
  const url = baseWalletV1Api + 'validate-address';
  const chainCanon = canonicalWithdrawalChainFromUiCode(chain) || String(chain || "").trim();
  const body = { address, chain: chainCanon };
  if (tokenAssetId) body.tokenAssetId = tokenAssetId;
  return ApiCallPost(url, body, bearerHeaders());
}

export function mapWithdrawError(resOrErr, statusHint) {
  const code = resOrErr && resOrErr.code;
  const msg = (resOrErr && (resOrErr.message || resOrErr.msg)) || "";
  const s = String(msg).toLowerCase();
  if (code === "PASSKEY_CREDENTIAL_REQUIRED") {
    return "Passkey verification is required. Tap Verify to try again.";
  }
  if (code === "PASSKEY_CHALLENGE_EXPIRED") {
    return "Passkey session expired. Tap Verify to try again.";
  }
  if (code === "PASSKEY_NOT_FOUND") {
    return "No matching passkey on this account. Register a passkey in security settings.";
  }
  if (code === "PASSKEY_INVALID") {
    return "Passkey verification failed. Please try again.";
  }
  if (code === "DAILY_LIMIT_EXCEEDED") {
    return "Daily withdrawal limit reached. Try again after the limit resets.";
  }
  if (code === "INSUFFICIENT_BALANCE") {
    return resOrErr?.message || "Insufficient balance for this withdrawal.";
  }
  if (code === "KYC_REQUIRED") {
    return "Identity verification is required before withdrawing. Please complete KYC.";
  }
  if (code === "ADDRESS_NOT_WHITELISTED" || code === "ADDRESS_WHITELISTED_ONLY") {
    return resOrErr?.message || "This address is not in your address book. Add it in saved addresses and try again.";
  }
  if (code === "NO_VERIFICATION_METHOD") {
    return "Please add at least one withdrawal verification method to continue.";
  }
  if (statusHint === 403 || s.includes("travel") || s.includes("blocked")) {
    return "This withdrawal was blocked by Travel Rule / compliance. Contact support if you need help.";
  }
  if (statusHint === 404 || s.includes("not found")) {
    return "The selected coin or network was not found. Refresh and try again.";
  }
  if (statusHint === 400 || statusHint === 422) {
    return resOrErr?.message || "Please check your input and try again.";
  }
  if (statusHint === 500 || statusHint === 503 || s.includes("fireblocks") || s.includes("temporarily")) {
    return "Service temporarily unavailable. Please try again in a few minutes.";
  }
  return resOrErr?.message || "Withdrawal could not be completed. Please try again.";
}
